import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import { loadSessionData } from './solutionSession.thunk';
import { logout } from '../session/logoutThunk';

type UserInput = Readonly<
  | {
      type: 'SINGLE_INPUT';
      value: string;
    }
  | {
      type: 'MULTIPLE_INPUT';
      value: string[];
    }
  | {
      type: 'MULTIPLE_INPUT_MAP';
      value: Record<string, string>;
    }
>;

type SolutionSessionState = Readonly<{
  userMessage: string;
  inputBoxMessage: string;
  chatMode: boolean;

  userInput?: UserInput;

  loading: boolean;
  error?: string;
  hasClearedSession: boolean;
}>;

export const initialState: SolutionSessionState = {
  loading: true,

  userMessage: '',
  inputBoxMessage: '',
  chatMode: false,
  hasClearedSession: false,
};

export const solutionSessionSlice = createSlice({
  name: 'solutionSession',
  initialState,
  reducers: {
    setUserMessage: (state, action: PayloadAction<string>) => {
      state.userMessage = action.payload;
    },
    setInputBoxMessage: (state, action: PayloadAction<string>) => {
      state.inputBoxMessage = action.payload;
    },
    setChatMode: (state, action: PayloadAction<boolean>) => {
      state.chatMode = action.payload;
    },
    setUserInput: (state, action: PayloadAction<UserInput | undefined>) => {
      state.userInput = action.payload;
    },
    resetSolutionSessionState: (state) => {
      state.userMessage = initialState.userMessage;
      state.inputBoxMessage = initialState.inputBoxMessage;
      state.chatMode = initialState.chatMode;
      state.userInput = initialState.userInput;
      state.loading = initialState.loading;
      state.error = initialState.error;
    },
    setHasClearedSession: (state, action: PayloadAction<boolean>) => {
      state.hasClearedSession = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // LOGOUT
      .addCase(logout, (state) => {
        state.loading = false;
        state.error = undefined;
      })
      // LOAD SESSION DATA
      .addCase(loadSessionData.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadSessionData.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(loadSessionData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const {
  setUserMessage,
  setInputBoxMessage,
  setChatMode,
  setUserInput,
  resetSolutionSessionState,
  setHasClearedSession,
} = solutionSessionSlice.actions;

// Reducer
export const solutionSessionReducer = solutionSessionSlice.reducer;

// Selectors
// only add selectors when you need them to do some sort of logic. Otherwise, just use the state directly
